import { Component, computed, inject, input, InputSignal, signal } from "@angular/core";
import { toObservable, toSignal } from "@angular/core/rxjs-interop";
import { ActivatedRoute } from "@angular/router";
import { firstValueFrom, of, switchMap } from "rxjs";
import { TranslateService } from '@ngx-translate/core';

import {
  LiquidAnnotationsListComponent,
  LiquidIndexComponent,
  LiquidInfoComponent,
  PopupConfig
} from "src/index";

import {
  ConfigurationService,
  LiquidViewerIndexService,
  LiquidViewerService,
  LoadingService,
  ViewerService,
  RoutingService,
  PopupService,
  LiquidViewerAnnotationListService,
} from "src/index/services.index";
import {
  LiquidPageConfiguration,
  LiquidPageAnnotation,
} from "./liquid-page/liquid-page.models";
import {
  LiquidViewerAnnotation,
  LiquidViewerAnnotationType,
  LiquidViewerIndexItem,
  LiquidViewerIndexParagraph,
} from "./liquid-viewer.models";

@Component({
  selector: 'meta-liquid-viewer',
  templateUrl: './liquid-viewer.component.html',
  styleUrls: ['./liquid-viewer.component.css']
})
export class LiquidViewerComponent {
  configurationService = inject(ConfigurationService);
  loadingService = inject(LoadingService);
  route = inject(ActivatedRoute);
  routingService = inject(RoutingService);
  translateService = inject(TranslateService);
  popupService = inject(PopupService);

  liquidViewerService = inject(LiquidViewerService);
  viewerService = inject(ViewerService);
  liquidViewerIndexService = inject(LiquidViewerIndexService);
  liquidViewerAnnotationsListService = inject(LiquidViewerAnnotationListService);

  /* --------- IDs from route -------------- */
  $mainId: InputSignal<string> = input('', { alias: 'id' });
  $secondaryId: InputSignal<string> = input('', { alias: 'secondaryId' });
  $contentId: InputSignal<string> = input('', { alias: 'contentId' });

  /* --------- Configurations -------------- */
  $toolbarConfig = computed(() => this.liquidViewerService.$toolbarConfig());
  $viewerConfig = computed(() => this.liquidViewerService.$viewerConfig());
  $pageConfiguration = computed(() => ({
    ...this.liquidViewerService.$pageSettings(),
    ...this.viewerService.$accessibilitySettings(),
    lineHeight: this.viewerService.$accessibilitySettings().line,
    fontFamily: this.viewerService.$accessibilitySettings().family,
    fontSize: this.viewerService.$accessibilitySettings().size,
  } as LiquidPageConfiguration));

  /* --------- Variables ---------------- */
  $createClipping = signal<boolean>(false);
  $toolbarPanelOpened = signal<boolean>(false);
  $clippingToShow = signal<{ title: string; src: string; } | undefined>(undefined);
  $linkToShow = signal<{ url: string; } | undefined>(undefined);
  $noteToShow = signal<{ title: string; text: string; } | undefined>(undefined);
  $annotationPopupConfig = signal<PopupConfig>({
    size: 'medium',
    type: 'alert',
    subtype: 'confirm',
    alertConfig: {
      header: { title: 'N.D.' },
    },
    labels: {
      confirm: 'Save'
    },
    callbacks: {
      cancel: () => {
        this.$clippingToShow.set(undefined);
        this.$linkToShow.set(undefined);
        this.$noteToShow.set(undefined);
      }
    }
  });

  /* --------- Progress bar -------------- */
  $currentProgress = computed(() => {
    const currentIndexItem = this.liquidViewerIndexService.$currentIndexItem();
    const pagesCounter = this.liquidViewerIndexService.$pagesCounter();
    if (currentIndexItem && currentIndexItem.pageNumber) {
      return Number.parseFloat(((currentIndexItem.pageNumber * 100) / pagesCounter).toFixed(4));
    }

    return 0;
  })
  $progressBarSteps = computed(() => {
    const pages = this.liquidViewerIndexService.$pages();
    const pagesCounter = this.liquidViewerIndexService.$pagesCounter();
    const steps: Array<{ width: number }> = [];
    if (pagesCounter <= 1) {
      return steps;
    }
    const stepDelta = 100 / pagesCounter;

    for (let page of Object.values(pages)) {
      if (page.pageNumber) {
        if (page.pageNumber === pagesCounter) {
          steps.push({ width: 100 });
        } else {
          steps.push({ width: Number.parseFloat((stepDelta * page.pageNumber).toFixed(4)) });
        }
      }
    }

    return steps.sort((a, b) => a.width >= b.width ? 1 : -1);
  });
  $progressBarTooltip = computed(() => {
    const currentIndexItem = this.liquidViewerIndexService.$currentIndexItem();
    if (currentIndexItem) {
      return currentIndexItem.title;
    }

    return undefined;
  })

  /* --------- Main content -------------- */
  mainContent$ = toObservable(this.$mainId).pipe(
    switchMap((mainId) => {
      return this.liquidViewerService.getMainContent(mainId)
    })
  )
  $mainContent = toSignal(this.mainContent$);
  $updatedAnnotations = signal<LiquidViewerAnnotation[] | undefined>(undefined);
  $mainContentAnnotations = computed(() => {
    if (this.$updatedAnnotations()) {
      return this.$updatedAnnotations();
    }
    if (this.$mainContent() && this.$mainContent()?.annotations) {
      return this.$mainContent()?.annotations;
    }
    return [];
  })

  /* --------- Page content -------------- */
  pageContent$ = toObservable(this.$contentId).pipe(
    switchMap((contentId) => {
      if (this.$mainId() && this.$secondaryId() && contentId) {
        return this.liquidViewerService.getPageContent(this.$mainId(), this.$secondaryId(), contentId);
      }
      return of(undefined);
    }),
  )
  $pageContent = toSignal(this.pageContent$);

  /* --------- Page annotations -------------- */
  $pageAnnotations = computed(() => {
    if (this.$mainContentAnnotations()) {
      const filtered = this.$mainContentAnnotations()?.
        filter(item => item.mainId === this.$mainId() &&
          item.secondaryMainId === this.$secondaryId() &&
          item.contentId === this.$contentId() &&
          ['highlight', 'note', 'link'].indexOf(item.type) !== -1
        )
        .map(item => ({
          id: item.id!,
          title: item.title,
          text: item.data?.text!,
          color: item.data?.color!,
          underline: ['note', 'link'].indexOf(item.type) !== -1,
          type: item.type,
          data: item.data,
        }))
      return filtered ?? [];
    }
    return [];
  })
  $pageBookmark = computed(() => {
    if (this.$mainContentAnnotations()) {
      return this.$mainContentAnnotations()?.
        find(item => item.mainId === this.$mainId() &&
          item.secondaryMainId === this.$secondaryId() &&
          item.contentId === this.$contentId() &&
          item.type === 'bookmark'
        )
    }
    return undefined;
  })

  /* --------- Toolbar tools -------------- */
  $toolbarTools = computed(() => [
    {
      ...this.configurationService.viewerToolbarTools.info,
      panelComponent: {
        component: LiquidInfoComponent,
        dynamicInputs: {
          mainContent: this.$mainContent()
        }
      }
    },
    {
      ...this.configurationService.viewerToolbarTools.index,
      panelComponent: {
        component: LiquidIndexComponent,
        dynamicInputs: {
          index: this.$mainContent()?.index,
          currentIndexItemId: this.$contentId(),
        },
        dynamicOutputs: {
          indexItemClicked: (event) => this.handleLiquidViewerIndexOutput(event as LiquidViewerIndexItem | LiquidViewerIndexParagraph),
        },
      }
    },
    {
      ...this.configurationService.viewerToolbarTools.workbook,
      panelComponent: {
        component: LiquidAnnotationsListComponent,
        dynamicInputs: {
          annotations: this.$mainContentAnnotations(),
          pageBookmark: this.$pageBookmark(),
        },
        dynamicOutputs: {
          openAnnotation: (event) => this.openAnnotation(event as LiquidViewerAnnotation),
          deleteAnnotation: (event) => this.deleteAnnotation(event as LiquidViewerAnnotation),
          createBookmark: () => this.createBookmark(),
          createClipping: () => this.$createClipping.set(true),
        }
      },
    },
    this.configurationService.viewerToolbarTools.accessibility,
    this.configurationService.viewerToolbarTools.settings,
  ]);

  $toolbarLinks = computed(() => this.configurationService.getViewerToolbarSecondaryTools());

  /*-------------- Navigation ---------------*/
  goPrevious() {
    const previous = this.liquidViewerIndexService.$previous() as LiquidViewerIndexItem;
    if (previous) {
      this.goTo(previous);
    }
  }

  hasPrevious() {
    return this.liquidViewerIndexService.$previous();
  }

  goNext() {
    const next = this.liquidViewerIndexService.$next() as LiquidViewerIndexItem;
    if (next) {
      this.goTo(next);
    }
  }

  hasNext() {
    return this.liquidViewerIndexService.$next();
    /* if (this.viewerIndexService.$currentIndexItem()?.isSurvey &&
      !this.viewerIndexService.$currentIndexItem()?.optional &&
      !this.viewerIndexService.$currentContentCompleted()) {
      return false
    } */
    /* const hn = this.hasNext();
    if (!hn) {
      return typeof this.config.disableCourseCompletedPage === 'undefined' || this.config.disableCourseCompletedPage === false;
    } else {
      return true;
    } */
  }

  handleLiquidViewerIndexOutput(item: LiquidViewerIndexItem | LiquidViewerIndexParagraph | null) {
    if (item) {
      this.goTo(item);
    }
  }

  private goTo(item: LiquidViewerIndexItem | LiquidViewerIndexParagraph) {
    if (item.type === 'page' && item.mainId && item.secondaryMainId && item.id) {
      this.routingService.goToLiquidViewer(item.mainId, item.secondaryMainId, item.id);
    }
    if (item.type === 'paragraph') {
      // TODO:
    }
  }

  /*--------------- Annotations --------------------- */
  async deleteAnnotation(annotation: LiquidViewerAnnotation) {
    if (!annotation.messageDeletePopup) {
      return;
    }
    const deleteMsgTranslated = await firstValueFrom(this.translateService.get(
      annotation.messageDeletePopup,
      { value: annotation.title }
    ));
    this.popupService.confirm(`<span>${deleteMsgTranslated}</span>`, {
      size: 'medium',
      callbacks: {
        confirm: () => {
          this.loadingService.show();
          firstValueFrom(this.liquidViewerAnnotationsListService.deleteAnnotation(annotation))
            .then((annotations) => this.$updatedAnnotations.set(annotations))
            .finally(() => this.loadingService.hide());
        }
      }
    });
  }

  async openAnnotation(annotation: LiquidViewerAnnotation) {
    if (annotation.mainId && annotation.secondaryMainId && annotation.contentId) {
      this.routingService.goToLiquidViewer(annotation.mainId, annotation.secondaryMainId, annotation.contentId);
    }
    if (annotation.type === 'clipping') {
      const titleTranslated = await firstValueFrom(this.translateService.get('Edit clipping'));
      this.$annotationPopupConfig.update(cv => {
        cv.alertConfig!.header!.title = titleTranslated;
        cv.callbacks = {
          ...cv.callbacks,
          confirm: () => {
            const clippingTitle = (document.getElementById('clippingTitle') as HTMLInputElement)?.value;
            this.$clippingToShow.set(undefined);
            if (annotation.title !== clippingTitle) {
              annotation.title = clippingTitle;
              this.updateAnnotation(annotation);
            }
          }
        }
        return cv;
      })
      this.$clippingToShow.set({
        title: annotation.title,
        src: annotation.data?.clippingSrc!,
      })
    }

    if (annotation.type === 'link') {
      const titleTranslated = await firstValueFrom(this.translateService.get('Edit link'));
      this.$annotationPopupConfig.update(cv => {
        cv.alertConfig!.header!.title = titleTranslated;
        cv.callbacks = {
          ...cv.callbacks,
          confirm: () => {
            const linkUrl = (document.getElementById('linkUrl') as HTMLInputElement)?.value;
            this.$linkToShow.set(undefined);
            if (annotation.title !== linkUrl) {
              annotation.title = linkUrl;
              if (annotation.id) {
                this.updateAnnotation(annotation);
              } else {
                this.createAnnotation(annotation);
              }
            }
          }
        }
        return cv;
      })
      this.$linkToShow.set({
        url: annotation.title,
      })
    }

    if (annotation.type === 'note') {
      const titleTranslated = await firstValueFrom(this.translateService.get('Edit note'));
      this.$annotationPopupConfig.update(cv => {
        cv.alertConfig!.header!.title = titleTranslated;
        cv.callbacks = {
          ...cv.callbacks,
          confirm: () => {
            const noteTitle = (document.getElementById('noteTitle') as HTMLInputElement)?.value;
            const noteText = (document.getElementById('noteText') as HTMLInputElement)?.value;
            this.$noteToShow.set(undefined);
            if (annotation.title !== noteTitle || annotation.data?.textNote !== noteText) {
              annotation.title = noteTitle;
              annotation.data!.textNote = noteText;
              if (annotation.id) {
                this.updateAnnotation(annotation);
              } else {
                this.createAnnotation(annotation);
              }
            }
          }
        }
        return cv;
      })
      this.$noteToShow.set({
        title: annotation.title,
        text: annotation.data?.textNote || '',
      })
    }
  }

  createBookmark() {
    const newAnnotation: LiquidViewerAnnotation = {
      title: this.liquidViewerIndexService.$currentIndexItem()?.title || `Bookmark ${this.$contentId()}`,
      mainId: this.$mainId(),
      secondaryMainId: this.$secondaryId(),
      contentId: this.$contentId(),
      type: "bookmark",
      icon: 'star-fill',
    };
    this.createAnnotation(newAnnotation);
  }

  createClipping(clipping: string, clippingTitle: string) {
    const newAnnotation: LiquidViewerAnnotation = {
      title: clippingTitle,
      mainId: this.$mainId(),
      secondaryMainId: this.$secondaryId(),
      contentId: this.$contentId(),
      type: "clipping",
      icon: 'crop',
      data: {
        clippingSrc: clipping,
      }
    };
    this.createAnnotation(newAnnotation);
  }

  createPageAnnotation(evt: LiquidPageAnnotation) {
    const newAnnotation: LiquidViewerAnnotation = {
      title: evt.text,
      mainId: this.$mainId(),
      secondaryMainId: this.$secondaryId(),
      contentId: this.$contentId(),
      type: evt.type as LiquidViewerAnnotationType,
      icon: evt.type,
      data: {
        color: evt.color,
        text: evt.text || evt.title,
      }
    };

    if (newAnnotation.type === 'highlight') { // Ho già tutti i dati necessari
      this.createAnnotation(newAnnotation);
    } else { // Devo prima far inserire i dati all'utente
      this.openAnnotation(newAnnotation);
    }
  }

  openPageAnnotation(evt: LiquidPageAnnotation) {
    const relatedViewerAnnotation = this.$mainContentAnnotations()?.find(item => item.id === evt.id);
    if (relatedViewerAnnotation) {
      this.openAnnotation(relatedViewerAnnotation);
    }
  }

  updatePageAnnotation(evt: LiquidPageAnnotation) {
    const relatedViewerAnnotation = this.$mainContentAnnotations()?.find(item => item.id === evt.id);
    if (relatedViewerAnnotation) {
      this.loadingService.show();
      const updatedAnnotation = {
        ...relatedViewerAnnotation,
        data: { ...evt.data },
      };
      firstValueFrom(this.liquidViewerAnnotationsListService.updateAnnotation(updatedAnnotation))
        .then((annotations) => this.$updatedAnnotations.set(annotations))
        .finally(() => this.loadingService.hide());
    }
  }

  updateAnnotation(annotation: LiquidViewerAnnotation) {
    this.loadingService.show();
    firstValueFrom(this.liquidViewerAnnotationsListService.updateAnnotation(annotation))
      .then((annotations) => this.$updatedAnnotations.set(annotations))
      .finally(() => this.loadingService.hide());
  }

  createAnnotation(annotation: LiquidViewerAnnotation) {
    this.loadingService.show();
    firstValueFrom(this.liquidViewerAnnotationsListService.createAnnotation(annotation))
        .then((annotations) => this.$updatedAnnotations.set(annotations))
        .finally(() => this.loadingService.hide());
  }

  /*--------------- Events from Liquid Page ---------------*/
  async handleNewClipping(clipping: string) {
    this.$createClipping.set(false);

    const titleTranslated = await firstValueFrom(this.translateService.get('New clipping'));
    this.$annotationPopupConfig.update(cv => {
      cv.alertConfig!.header!.title = titleTranslated;
      cv.callbacks = {
        ...cv.callbacks,
        confirm: () => {
          const clippingTitle = (document.getElementById('clippingTitle') as HTMLInputElement)?.value;
          this.$clippingToShow.set(undefined);
          this.createClipping(clipping, clippingTitle);
        }
      }
      return cv;
    })
    const titleTranslated2 = await firstValueFrom(this.translateService.get('Clipping'));
    this.$clippingToShow.set({
      title: `${titleTranslated2} ${this.$contentId()}`,
      src: clipping,
    })
  }

  deletePageAnnotation(evt: LiquidPageAnnotation) {
    const relatedViewerAnnotation = this.$mainContentAnnotations()?.find(item => item.id === evt.id);
    if (relatedViewerAnnotation) {
      this.deleteAnnotation(relatedViewerAnnotation);
    }
  }
}
